import { useMessageEvents } from '@gov-nx/core/events';
import { useRezaLocale } from '../useRezaLocale';
import {
	rezaSendByDataboxQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useDataBoxStoreLists } from '@gov-nx/store';
import { type DataBoxFields, useDataboxDefinition } from '@gov-nx/utils/common';
import {
	useFormBuilder,
	getAllDataboxTypes,
	type FormDefinition2,
} from '@gov-nx/core/service';
import type { DataBoxFormData } from '@gov-nx/ui/types';
import { useEffect } from 'react';

export interface UseRezaSendByDataBoxForm {
	formDefinition: FormDefinition2<DataBoxFormData, DataBoxFields>;
	hasDataBoxes: boolean;
	isLoading: boolean;
	formId: string;
	strings: {
		sendByDataboxButton: string;
	};
	onSubmit: () => void;
}

const REZA_SEND_BY_DATABOX_FORM_ID = 'reza-send-by-databox-form';

export const useRezaSendByDataBoxForm = (
	code: string
): UseRezaSendByDataBoxForm => {
	const { t } = useRezaLocale();
	const { toastMessageError, toastMessageSuccess } = useMessageEvents();
	const { axiosInstance } = useAxiosInstance();
	const { getConnectedDataBoxesByTypes } = useDataBoxStoreLists();
	const hasDataBoxes =
		getConnectedDataBoxesByTypes(getAllDataboxTypes()).length > 0;

	const sendByDataboxMutation = usePoMutation({
		mutationFn: rezaSendByDataboxQuery,
		onSuccess: () => {
			toastMessageSuccess(t('overview.form.sendByDatabox.uspesne-odeslano'));
			formDefinition.formReset();
		},
		onError: () => toastMessageError(),
	});
	const isLoading = sendByDataboxMutation.isLoading;

	const databox = useDataboxDefinition({
		label: t('overview.form.databox.label'),
		isDisabled: isLoading,
		types: getAllDataboxTypes(),
	});

	const formDefinition = useFormBuilder<DataBoxFormData, DataBoxFields>(
		databox
	);

	useEffect(() => {
		if (!hasDataBoxes) {
			formDefinition.formMethods.setValue('datovaSchrankaId', '');
		}
	}, [formDefinition.formMethods, hasDataBoxes]);

	const onSubmit = formDefinition.formMethods.handleSubmit((formData) => {
		const { datovaSchrankaId, naseCisloJednaci, naseSpisovaZnacka } = formData;

		sendByDataboxMutation.mutate({
			axiosInstance,
			code,
			body: {
				odesilatelDSId: datovaSchrankaId,
				naseCisloJednaci,
				naseSpisovaZnacka,
			},
		});
	});

	return {
		formDefinition,
		hasDataBoxes,
		isLoading,
		formId: REZA_SEND_BY_DATABOX_FORM_ID,
		strings: {
			sendByDataboxButton: t('detail.buttons.ziskat-datovou-schranku'),
		},
		onSubmit,
	};
};
